import { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Upload, FileText, CheckCircle, XCircle, AlertCircle, Download, RefreshCw } from 'lucide-react';
import {
  fetchImportModelsThunk,
  validateCSVThunk,
  importCSVThunk
} from '../../../redux/admin/adminActionThunks';

import { setCsvWizardStep, setCsvWizardModel, setCsvWizardFile, setCsvWizardFieldMapping, resetCsvWizard } from '../../../redux/admin/adminSlice';

const CSVImportSystem = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Redux state
  const {
    importModels,
    csvValidation,
    csvImportResult,
    loading,
    errors,
    csvWizard
  } = useSelector(state => state.admin);

  // Destructure wizard state from Redux
  const { step, selectedModel, csvFile, fieldMapping } = csvWizard;

  // Local-only state for CSV preview (not persisted)
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // 1️⃣ ADD: Local File state to avoid Redux serialization issues
  const [localCsvFile, setLocalCsvFile] = useState(null);

  // Parse CSV file locally (preview only, still update Redux mapping)
  const parseCSV = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result;
      if (!text) return;

      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length === 0) return;

      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const rows = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        return row;
      });

      setCsvHeaders(headers);
      setCsvData(rows.slice(0, 100)); // Preview first 100 rows

      // Auto-map fields
      if (selectedModel) {
        const autoMapping = {};
        selectedModel.fields.forEach(field => {
          const matchingHeader = headers.find(h =>
            h.toLowerCase() === field.name.toLowerCase() ||
            h.toLowerCase().replace(/[_\s]/g, '') === field.name.toLowerCase().replace(/[_\s]/g, '')
          );
          if (matchingHeader) {
            autoMapping[field.name] = matchingHeader;
          }
        });
        dispatch(setCsvWizardFieldMapping(autoMapping));
      }
    };

    reader.onerror = () => {
      alert(t('admin.importCsv.errors.readingFile'));
    };

    reader.readAsText(file);
  }, [selectedModel, dispatch, t]);

  // 2️⃣ FIX: Handle file upload - store File in local state, only metadata in Redux
  const handleFileUpload = useCallback((e) => {
    const file = e.target.files?.[0];

    if (!file) {
      alert(t('admin.importCsv.errors.noFileSelected'));
      return;
    }

    if (file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
      alert(t('admin.importCsv.errors.invalidFileType'));
      return;
    }

    setLocalCsvFile(file); // Store real File object locally
    dispatch(setCsvWizardFile(file)); // Store metadata only in Redux
    parseCSV(file);
    dispatch(setCsvWizardStep(3));
  }, [dispatch, parseCSV, t]);

  // 3️⃣ FIX: Validate CSV - use local File object
  const validateCSV = useCallback(async () => {
    if (!selectedModel || !localCsvFile) {
      console.warn('Missing selectedModel or localCsvFile:', { selectedModel, localCsvFile });
      return;
    }

    try {
      const result = await dispatch(validateCSVThunk({
        file: localCsvFile, // Use local File object
        target_models: selectedModel.registry_key,
        field_mapping: fieldMapping
      }));

      if (!validateCSVThunk.fulfilled.match(result)) {
        console.warn('Validation failed:', result);
      }
      // Step advance is handled in slice
    } catch (error) {
      console.warn('Error in validateCSV:', error);
    }
  }, [dispatch, selectedModel, localCsvFile, fieldMapping]);

  // 4️⃣ FIX: Import CSV - use local File object
  const importCSV = useCallback(async () => {
    if (!selectedModel || !localCsvFile) {
      console.warn('Missing selectedModel or localCsvFile:', { selectedModel, localCsvFile });
      return;
    }

    try {
      const result = await dispatch(importCSVThunk({
        file: localCsvFile, // Use local File object
        target_models: selectedModel.registry_key,
        field_mapping: fieldMapping
      }));

      if (!importCSVThunk.fulfilled.match(result)) {
        console.warn('Import failed:', result);
      }
      // Step advance is handled in slice
    } catch (error) {
      console.warn('Error in importCSV:', error);
    }
  }, [dispatch, selectedModel, localCsvFile, fieldMapping]);

  // 5️⃣ FIX: Reset wizard - also clear local File state
  const reset = useCallback(() => {
    dispatch(resetCsvWizard());
    setCsvData([]);
    setCsvHeaders([]);
    setLocalCsvFile(null); // Clear local File state
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  }, [dispatch]);

  // Load models on mount
  useEffect(() => {
    if (!isInitialized && !importModels?.length && !loading.importModels) {
      //console.log('Initializing import models...');
      setIsInitialized(true);
      dispatch(fetchImportModelsThunk());
    }
  }, [dispatch, isInitialized, importModels?.length, loading.importModels]);

  // Computed values
  const mappingComplete = useMemo(() => {
    if (!selectedModel) return false;
    const requiredFields = selectedModel.fields.filter(f => f.required);
    return requiredFields.every(field => fieldMapping[field.name]);
  }, [selectedModel, fieldMapping]);

  const hasErrors = csvValidation && csvValidation.errors
    ? csvValidation.errors.filter(e => e.severity === 'error').length > 0
    : false;

  // Current error
  const currentError = errors.importModels || errors.csvValidation || errors.csvImport;


  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6 min-h-0">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('admin.importCsv.title')}</h1>
        <p className="text-gray-600 dark:text-gray-300 mb-6">{t('admin.importCsv.subtitle')}</p>

        {/* Progress Steps -- 5 fixed-width steps (circle + label + 64px
            connector each) previously had no wrap or scroll container and
            no mobile variant, so steps silently ran off both edges of the
            viewport below ~640px with no way to see or reach them (found
            live during the 2026-07-17 mobile audit). Text labels now
            sm:-only; connectors shrink on mobile; overflow-x-auto is a
            safety net so nothing is ever fully unreachable even if it
            still doesn't all fit. */}
        <div className="flex items-center gap-1 sm:space-x-4 mb-8 overflow-x-auto pb-1">
          {[
            { num: 1, title: t('admin.importCsv.steps.selectModel'), active: step >= 1 },
            { num: 2, title: t('admin.importCsv.steps.uploadCsv'), active: step >= 2 },
            { num: 3, title: t('admin.importCsv.steps.mapFields'), active: step >= 3 },
            { num: 4, title: t('admin.importCsv.steps.validate'), active: step >= 4 },
            { num: 5, title: t('admin.importCsv.steps.import'), active: step >= 5 }
          ].map(({ num, title, active }) => (
            <div key={num} className="flex items-center flex-shrink-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 ${
                active ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}>
                {num}
              </div>
              <div className={`ml-2 hidden sm:block text-sm font-medium whitespace-nowrap ${active ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                {title}
              </div>
              {num < 5 && (
                <div className={`w-6 sm:w-16 h-1 mx-1 sm:mx-2 flex-shrink-0 ${active ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Error Alert */}
        {currentError && (
          <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 dark:border-red-800 p-4 mb-4 rounded">
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-500 dark:text-red-400 mr-2" />
              <div>
                <span className="font-medium text-red-900 dark:text-red-200">{t('admin.importCsv.errorLabel')}</span>
                <div className="text-red-800 dark:text-red-300 text-sm">{currentError}</div>
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Select Model */}
        {step === 1 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('admin.importCsv.step1.heading')}</h2>
            {loading.importModels ? (
              <div className="flex justify-center items-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {importModels?.map((model) => (
                  <button
                    key={model.registry_key}
                    onClick={() => {
                      dispatch(setCsvWizardModel(model));
                      dispatch(setCsvWizardStep(2));
                    }}
                    className="p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-left"
                  >
                    <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400 mb-2" />
                    <h3 className="font-medium text-gray-900 dark:text-white">{model.display_name}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{model.description}</p>
                    <p className="text-xs text-gray-400 mt-2">{t('admin.importCsv.step1.fieldsCount', { count: model.fields?.length || 0 })}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Upload CSV */}
        {step === 2 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('admin.importCsv.step2.heading')}</h2>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-12 text-center">
              <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <span className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
                  {t('admin.importCsv.step2.clickToUpload')}
                </span>
                <span className="text-gray-500 dark:text-gray-400">{t('admin.importCsv.step2.orDragAndDrop')}</span>
              </label>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{t('admin.importCsv.step2.csvOnly')}</p>
            </div>
            <div className="flex justify-between">
              <button
                onClick={() => dispatch(setCsvWizardStep(1))}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t('admin.importCsv.step2.back')}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Map Fields */}
        {step === 3 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('admin.importCsv.step3.heading')}</h2>

            {/* File info */}
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong>{t('admin.importCsv.step3.file')}</strong> {csvFile?.name}
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong>{t('admin.importCsv.step3.rows')}</strong> {csvData.length}
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong>{t('admin.importCsv.step3.columns')}</strong> {csvHeaders.length}
              </p>
            </div>

            {/* CSV Preview */}
            <div>
              <h3 className="font-medium mb-4 text-gray-900 dark:text-white">{t('admin.importCsv.step3.previewHeading')}</h3>
              <div className="max-h-64 overflow-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                    <tr>
                      {csvHeaders.map((header) => (
                        <th
                          key={header}
                          className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {csvData.slice(0, 10).map((row, idx) => (
                      <tr key={idx}>
                        {csvHeaders.map((header) => (
                          <td key={header} className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                            {String(row[header] || '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Field Mapping */}
            <div>
              <h3 className="font-medium mb-4 text-gray-900 dark:text-white">{t('admin.importCsv.step3.fieldMappingHeading')}</h3>
              <div className="space-y-4">
                {selectedModel?.fields?.map((field) => (
                  <div key={field.name} className="flex items-center space-x-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {field.display_name || field.name}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                    </div>
                    <select
                      value={fieldMapping[field.name] || ''}
                      onChange={(e) =>
                        dispatch(setCsvWizardFieldMapping({ ...fieldMapping, [field.name]: e.target.value }))
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">{t('admin.importCsv.step3.selectCsvColumn')}</option>
                      {csvHeaders.map((header) => (
                        <option key={header} value={header}>{header}</option>
                      ))}
                    </select>
                    {field.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{field.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => dispatch(setCsvWizardStep(2))}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t('admin.importCsv.step3.back')}
              </button>
              <button
                onClick={validateCSV}
                disabled={!mappingComplete || loading.csvValidation}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading.csvValidation ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {t('admin.importCsv.step3.validating')}
                  </>
                ) : (
                  t('admin.importCsv.step3.validateData')
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Validation Results */}
        {step === 4 && csvValidation && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('admin.importCsv.step4.heading')}</h2>

            {/* Summary */}
            <div className="grid gap-4 md:grid-cols-3">
              <div className="bg-green-50 dark:bg-green-500/10 p-4 rounded-lg">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-700 dark:text-green-400" />
                  <span className="ml-2 text-sm font-medium text-green-800 dark:text-green-300">{t('admin.importCsv.step4.validRows')}</span>
                </div>
                <p className="text-2xl font-bold text-green-900 dark:text-green-200 mt-1">{csvValidation.valid_rows}</p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-500/10 p-4 rounded-lg">
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-blue-400" />
                  <span className="ml-2 text-sm font-medium text-blue-800 dark:text-blue-300">{t('admin.importCsv.step4.totalRows')}</span>
                </div>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-200 mt-1">{csvValidation.total_rows}</p>
              </div>
              <div className="bg-red-50 dark:bg-red-500/10 p-4 rounded-lg">
                <div className="flex items-center">
                  <XCircle className="h-5 w-5 text-red-400" />
                  <span className="ml-2 text-sm font-medium text-red-800 dark:text-red-300">{t('admin.importCsv.step4.errorsLabel')}</span>
                </div>
                <p className="text-2xl font-bold text-red-900 dark:text-red-200 mt-1">{csvValidation.errors?.length || 0}</p>
              </div>
            </div>

            {/* Errors */}
            {csvValidation.errors && csvValidation.errors.length > 0 && (
              <div>
                <h3 className="font-medium mb-4 text-gray-900 dark:text-white">{t('admin.importCsv.step4.validationErrorsHeading')}</h3>
                <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('admin.importCsv.step4.tableRow')}</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('admin.importCsv.step4.tableField')}</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('admin.importCsv.step4.tableValue')}</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('admin.importCsv.step4.tableError')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {csvValidation.errors.map((error, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{error.row}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{error.field}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{String(error.value || '')}</td>
                          <td className="px-4 py-2 text-sm text-red-600 dark:text-red-400">{error.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Warning */}
            {hasErrors && (
              <div className="bg-yellow-50 dark:bg-yellow-500/10 border-l-4 border-orange-600 dark:border-yellow-700 p-4 mb-4 rounded">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 mr-2" />
                  <div>
                    <span className="font-medium text-yellow-900 dark:text-yellow-200">{t('admin.importCsv.step4.someRowsHaveErrors')}</span>
                    <div className="text-yellow-800 dark:text-yellow-300 text-sm">{t('admin.importCsv.step4.canProceedValidOnly')}</div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <button
                onClick={() => dispatch(setCsvWizardStep(3))}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t('admin.importCsv.step4.backToMapping')}
              </button>
              <button
                onClick={importCSV}
                disabled={loading.csvImport}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading.csvImport ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {t('admin.importCsv.step4.importing')}
                  </>
                ) : hasErrors ? (
                  t('admin.importCsv.step4.importValidRowsOnly')
                ) : (
                  t('admin.importCsv.step4.importData')
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 5: Import Results */}
        {step === 5 && csvImportResult && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('admin.importCsv.step5.heading')}</h2>

            {/* Summary */}
            <div className="grid gap-4 md:grid-cols-3">
              <div className="bg-green-50 dark:bg-green-500/10 p-4 rounded-lg">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-700 dark:text-green-400" />
                  <span className="ml-2 text-sm font-medium text-green-800 dark:text-green-300">{t('admin.importCsv.step5.imported')}</span>
                </div>
                <p className="text-2xl font-bold text-green-900 dark:text-green-200 mt-1">{csvImportResult.imported}</p>
              </div>
              <div className="bg-yellow-50 dark:bg-yellow-500/10 p-4 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                  <span className="ml-2 text-sm font-medium text-yellow-800 dark:text-yellow-300">{t('admin.importCsv.step5.skipped')}</span>
                </div>
                <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-200 mt-1">{csvImportResult.skipped}</p>
              </div>
              <div className="bg-red-50 dark:bg-red-500/10 p-4 rounded-lg">
                <div className="flex items-center">
                  <XCircle className="h-5 w-5 text-red-400" />
                  <span className="ml-2 text-sm font-medium text-red-800 dark:text-red-300">{t('admin.importCsv.step5.failed')}</span>
                </div>
                <p className="text-2xl font-bold text-red-900 dark:text-red-200 mt-1">{csvImportResult.failed}</p>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-500/10 p-4 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                <strong>{t('admin.importCsv.step5.auditLogId')}</strong> {csvImportResult.audit_log_id}
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                {t('admin.importCsv.step5.auditLogNote')}
              </p>
            </div>

            <div className="flex justify-between">
              <button
                onClick={reset}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t('admin.importCsv.step5.startNewImport')}
              </button>
              <button
                disabled
                title={t('admin.importCsv.step5.viewAuditLogTitle')}
                className="px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md opacity-50 cursor-not-allowed"
              >
                <Download className="w-4 h-4 mr-2" />
                {t('admin.importCsv.step5.viewAuditLog')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CSVImportSystem;